import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";
import { PaginationResponse } from "@/core/services/api/interfaces/PaginationResponse";
import ApiService from "@/core/services/ApiService";
import { AxiosRequestConfig } from "axios";
import {
  BankAccount,
  CreateBankAccount,
} from "@/core/services/api/interfaces/manager/BankAccount";
import { PortalService } from "@/core/services/api/interfaces/dict/PortalService";
import { FlexpspAccountPaymentType } from "@/interfaces/manager/PSPEntities";
import { CurrencyType } from "@/core/services/api/interfaces/manager/CurrencyUnit";

@Module
export default class CompanyAccountsModule extends VuexModule {
  pagination = new PaginationResponse<BankAccount>();
  selectedFiat: null | BankAccount = null;
  selectedCrypto: null | BankAccount = null;

  get getAccounts(): BankAccount[] {
    return this.pagination.data;
  }

  get getAccountsPagination(): PaginationResponse<BankAccount> {
    return this.pagination;
  }

  get getAccountsFiat(): BankAccount[] {
    return this.getAccounts.filter((account: BankAccount): boolean => {
      return CurrencyType.FIAT === account.currency_unit.currency_type;
    });
  }

  get getAccountsByCurrency() {
    return (currency: string | string[]): BankAccount[] => {
      if (!Array.isArray(currency)) {
        currency = [currency];
      }
      return this.getAccounts.filter((account: BankAccount): boolean =>
        currency.includes(account.currency_unit.value)
      );
    };
  }

  get getAccountsCrypto(): BankAccount[] {
    return this.getAccounts.filter((account: BankAccount): boolean => {
      return CurrencyType.CRYPTO === account.currency_unit.currency_type;
    });
  }

  get getAccountsFiatWithTemporaryIbans(): BankAccount[] {
    return this.getAccountsFiat.filter((account: BankAccount): boolean => {
      if (!account.temporary_ibans) {
        return false;
      }
      return account.temporary_ibans.length > 0;
    });
  }

  get getAccountByID() {
    return (id: null | number): null | BankAccount => {
      if (!id) {
        return null;
      }

      const accounts = this.getAccounts;

      for (const i in accounts) {
        const account = accounts[i];
        if (account.id === id) {
          return account;
        }
      }

      return null;
    };
  }

  get getAccountsFiatWithPortalServices(): BankAccount[] {
    return this.getAccountsFiat.filter((account: BankAccount): boolean => {
      return !!account.portal_service_active;
    });
  }

  get getAccountsFiatWithPortalService() {
    return (ps: PortalService | string): BankAccount[] => {
      return this.getAccountsFiatWithPortalServices.filter(
        (account: BankAccount): boolean => {
          const service = typeof ps === "string" ? ps : ps.value;

          return account.portal_service_active === service;
        }
      );
    };
  }

  get getAccountFiatPspPortalCurrent() {
    return (type: FlexpspAccountPaymentType): null | BankAccount => {
      for (const account of this.getAccountsFiat) {
        for (const pspAccount of account.psp_portal_accounts) {
          if (type !== pspAccount.account_type) {
            continue;
          }
          if (!pspAccount.account_fields.active) {
            continue;
          }

          return account;
        }
      }

      return null;
    };
  }

  get getAccountsFiatById() {
    return (id: number[] | number): BankAccount[] => {
      if (!Array.isArray(id)) {
        id = [id] as number[];
      }
      const list = id as number[];

      return this.getAccountsFiat.filter((account: BankAccount): boolean =>
        list.includes(account.id)
      );
    };
  }

  get getAccountFiatSelected(): null | BankAccount {
    return this.selectedFiat;
  }

  get getAccountCryptoSelected(): null | BankAccount {
    return this.selectedCrypto;
  }

  get getAccountFiatDefault(): null | BankAccount {
    const accounts = this.getAccountsFiat;
    for (const i in accounts) {
      const account = accounts[i];
      if (account.selected) {
        return account;
      }
    }

    return null;
  }

  get getAccountCryptoDefault(): null | BankAccount {
    const accounts = this.getAccountsCrypto;
    for (const i in accounts) {
      const account = accounts[i];
      if (account.selected) {
        return account;
      }
    }

    return null;
  }

  get getAccountFiatDefaultOrFirst() {
    return (except: number[] = []): null | BankAccount => {
      const defaultAccount = this.getAccountFiatDefault;

      if (defaultAccount && !except.includes(defaultAccount.id)) {
        return defaultAccount;
      }

      return this.getAccountFiatFirst(except);
    };
  }

  get getAccountCryptoDefaultOrFirst() {
    return (except: number[] = []): null | BankAccount => {
      const defaultAccount = this.getAccountCryptoDefault;

      if (defaultAccount && !except.includes(defaultAccount.id)) {
        return defaultAccount;
      }

      return this.getAccountCryptoFirst(except);
    };
  }

  get getAccountFiatFirst() {
    return (except: number[] = []): null | BankAccount => {
      const accounts = this.getAccountsFiat;
      if (!accounts.length) {
        return null;
      }
      for (const i in accounts) {
        const account = accounts[i];
        if (!except.includes(account.id)) {
          return account;
        }
      }

      return null;
    };
  }

  get getAccountCryptoFirst() {
    return (except: number[] = []): null | BankAccount => {
      const accounts = this.getAccountsCrypto;
      if (!accounts.length) {
        return null;
      }
      for (const i in accounts) {
        const account = accounts[i];
        if (!except.includes(account.id)) {
          return account;
        }
      }

      return null;
    };
  }

  @Action({ rawError: true })
  [Actions.MANAGER_COMPANY_ACCOUNTS_LOAD](params: {
    page?: number;
    per_page?: number;
  }) {
    return ApiService.manager.bank_account
      .list({
        params,
      } as AxiosRequestConfig)
      .then(ApiService.unwrapPaginationResponse<BankAccount>)
      .then(async (pagination) => {
        this.context.commit(Mutations.MANAGER_COMPANY_ACCOUNTS_SET, pagination);

        await this.context.dispatch(
          Actions.MANAGER_COMPANY_ACCOUNT_REFRESH_SELECTED
        );

        this.context.commit(
          Mutations.COMMON_REQUEST_DONE,
          Actions.MANAGER_COMPANY_ACCOUNTS_LOAD
        );
      });
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_COMPANY_ACCOUNT_SET_DEFAULT](account: BankAccount) {
    return ApiService.manager.bank_account
      .selectDefault(account)
      .then(() => {
        return this.context.dispatch(Actions.MANAGER_COMPANY_ACCOUNTS_LOAD, {
          page: 1,
          per_page: 100,
        });
      })
      .catch(console.error);
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_COMPANY_ACCOUNT_REFRESH_SELECTED]() {
    if (!this.selectedFiat || !this.getAccountByID(this.selectedFiat.id)) {
      this.context.commit(
        Mutations.MANAGER_COMPANY_ACCOUNT_SELECT,
        this.getAccountFiatDefaultOrFirst()
      );
    }
    if (!this.selectedCrypto || !this.getAccountByID(this.selectedCrypto.id)) {
      this.context.commit(
        Mutations.MANAGER_COMPANY_ACCOUNT_SELECT,
        this.getAccountCryptoDefaultOrFirst()
      );
    }
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_COMPANY_ACCOUNTS_CREATE](account: CreateBankAccount) {
    return ApiService.manager.bank_account
      .create(account)
      .then(ApiService.unwrapItemResponse<BankAccount>)
      .then(async (account) => {
        return this.context
          .dispatch(Actions.MANAGER_COMPANY_ACCOUNTS_LOAD, {
            page: 1,
            per_page: 100,
          })
          .then(() => account);
      });
  }

  @Action({ rawError: true })
  async [Actions.MANAGER_COMPANY_ACCOUNT_DOWNLOAD_RL](account: BankAccount) {
    return ApiService.manager.bank_account
      .downloadReferenceLetter(account)
      .then((response) =>
        ApiService.asFileDownload(
          response,
          `reference-letter-${account.id}.pdf`
        )
      )
      .catch(console.error);
  }

  @Mutation
  [Mutations.MANAGER_COMPANY_ACCOUNTS_SET](
    pagination: PaginationResponse<BankAccount>
  ) {
    this.pagination = pagination;
    for (const i in pagination.data) {
      const account = pagination.data[i];
      if (
        CurrencyType.FIAT === account.currency_unit.currency_type &&
        (!this.selectedFiat || account.id === this.selectedFiat.id)
      ) {
        this.selectedFiat = Object.assign(this.selectedFiat || {}, account);
      } else if (
        CurrencyType.CRYPTO === account.currency_unit.currency_type &&
        (!this.selectedCrypto || account.id === this.selectedCrypto.id)
      ) {
        this.selectedCrypto = Object.assign(this.selectedCrypto || {}, account);
      }
    }
  }

  @Mutation
  [Mutations.MANAGER_COMPANY_ACCOUNTS_RESET]() {
    this.pagination = new PaginationResponse<BankAccount>();
    this.selectedFiat = null;
    this.selectedCrypto = null;
  }

  @Mutation
  [Mutations.MANAGER_COMPANY_ACCOUNT_REPLACE](account: BankAccount) {
    const accounts = this.pagination.data;
    for (const i in accounts) {
      const dataAccount = accounts[i];
      if (dataAccount.id !== account.id) {
        continue;
      }
      this.pagination.data[i] = Object.assign(dataAccount, account);
    }
  }

  @Mutation
  [Mutations.MANAGER_COMPANY_ACCOUNT_REPLACE_OR_ADD](account: BankAccount) {
    let isFound = false;
    const accounts = this.pagination.data;
    for (const i in accounts) {
      const dataAccount = accounts[i];
      if (dataAccount.id !== account.id) {
        continue;
      }
      this.pagination.data[i] = Object.assign(accounts[i], account);
      isFound = true;
    }

    if (!isFound) {
      this.pagination.data.push(account);
    }
  }

  @Mutation
  [Mutations.MANAGER_COMPANY_ACCOUNT_SELECT](
    account: null | BankAccount | number
  ) {
    if (!account) {
      return;
    }
    if (typeof account !== "number") {
      if (CurrencyType.CRYPTO === account.currency_unit.currency_type) {
        this.selectedCrypto = Object.assign(this.selectedCrypto || {}, account);
      } else if (CurrencyType.FIAT === account.currency_unit.currency_type) {
        this.selectedFiat = Object.assign(this.selectedFiat || {}, account);
      }

      return;
    }
    const accounts = this.pagination.data;

    for (const i in accounts) {
      const iterated = accounts[i];
      if (account === iterated.id) {
        if (CurrencyType.CRYPTO === iterated.currency_unit.currency_type) {
          this.selectedCrypto = Object.assign(
            this.selectedCrypto || {},
            iterated
          );
        } else if (CurrencyType.FIAT === iterated.currency_unit.currency_type) {
          this.selectedFiat = Object.assign(this.selectedFiat || {}, iterated);
        }
        break;
      }
    }
  }
}
